<script>
import lazyLoadComponent from '@router/lazyload-component'

import Layout from '@layouts/main'
import formatDate, { formatDateRange } from '@utils/format-date'
import { defaultStatusFilter } from '@utils/filter-value-to-text'

import { PortalTarget } from 'portal-vue'
import { authComputed } from '@state/helpers'

export default {
  page() {
    return {
      title: `查看${this.$t('prepaid.BASE')}方案`,
      meta: [{ name: 'description', content: 'View Prepaid' }],
    }
  },
  filters: {
    formatDate,
    formatDateRange,
    defaultStatusFilter,
  },
  components: {
    Layout,
    PortalTarget,
    BaseStateLabel: () => lazyLoadComponent(import('@components/BaseStateLabel')),
    OrderForm: () => import('@components/PrepaidOrderForm'),
    PrepaidOrderForm: () => import('@components/Orders/PrepaidOrderForm'),
  },
  props: {
    products: {
      type: Array,
      required: true,
    },
    category: {
      type: Array,
      required: true,
    },
    prices: {
      type: Array,
      required: true,
    },
    defaultValue: {
      type: Object,
      default: () => { },
    },
    tagList: {
      type: Object,
      default: () => [],
    },
  },
  data() {
    return {
      delay: true,
      activeTabIndex: 0,
      displayValue: Object.assign({}, this.defaultValue),
      leftDisplayValue: {},
      sideTabList: [
        {
          icon: 'edit',
          title: `編輯${this.$t('prepaid.BASE')}方案`,
          name: 'edit',
          component: () => import('./DetailEdit'),
        },
        {
          icon: 'history',
          title: '購買紀錄',
          name: 'orders',
          component: () => import('./DetailOrders'),
        },
      ],
      drawerVisible: false,
      selectPrice: 0,
      componentKey: 0,
      appStyle: process.env.VUE_APP_BUILD_MODE === 'admin-app-build',
      openOverViewDialog: false,
    }
  },
  watch: {
    drawerVisible(value) {
      if (!value) {
        this.selectPrice = 0
      }
    },
  },
  created() {
    if (this.isOrderManager) this.sideTabList.shift()
  },
  mounted() {
    setTimeout(() => {
      this.delay = false
      if (this.$route.params.drawer) {
        this.$nextTick(() => {
          this.drawerVisible = true
        })
      }
    }, 500)
    this.leftDisplayValue = JSON.parse(JSON.stringify(this.displayValue))
  },
  computed: {
    ...authComputed,
    activeTab() {
      return this.sideTabList[this.activeTabIndex]
    },
    isOrderManager() {
      return !this.currentUserRoutes.includes('add-prepaid') && this.currentUserRoutes.includes('prepaid')
    },
    pagination() {
      return this.$route.params.pagination
    },
    filterData() {
      return this.$route.params.filterData
    },
    gridDataComputed() {
      let result = []
      if (this.leftDisplayValue && this.leftDisplayValue.prepaids) {
        this.leftDisplayValue.prepaids.forEach(item => {
          result.push({
            "sizeTitle": item.sizeTitle,
            "inventory": item.infinity === 1 ? '無限量' : `${item.inventory}個`,
          })
        })
      }
      return result
    }
  },
  methods: {
    async updateInfo() {
      const { currentRoute } = this.$router
      if (currentRoute.params && currentRoute.params.groupOrdering) {
        const result = await this.$store.dispatch('prepaid/fetchSingleGroup', currentRoute.params.groupOrdering)
        this.leftDisplayValue = JSON.parse(JSON.stringify(result))
        this.displayValue.infinity = result.infinity
        this.displayValue.groupInventoryTotal = result.groupInventoryTotal
      }
    },
    backToList() {
      this.$router.push({
        name: 'prepaid',
        params: {
          pagination: this.pagination,
          filterData: this.filterData,
        },
      })
    },
    handleUpdateOrders(isInstallments) {
      if (!this.isOrderManager) this.activeTabIndex = 1

      this.$nextTick(() => {
        let timeout = setInterval(() => {
          if (this.$refs.orders !== undefined) {
            clearInterval(timeout)
            this.$refs.orders.tryToFetchOrder()
          }
        }, 500)
      })
      if (!isInstallments) this.handleCloseDrawer()
    },
    handleCloseDrawer() {
      this.$refs.drawer.closeDrawer()
    },
    overviewDetailDialog() {
      this.openOverViewDialog = true
    }
  },
}
</script>

<template>
  <Layout>
    <div v-if="delay" v-loading="delay" style="height:100vh"></div>
    <template v-else>
      <div :class="$style.wrapper">
        <div :class="$style.sidebar">
          <div :class="$style.header">
            <div class="sidebar__left">
              <el-button class="is-round isStyleBase mb-4" @click="backToList">
                <BaseIcon name="arrow-left" />
                返回
              </el-button>
              <el-image class="image" :src="displayValue.image" fit="cover">
                <div slot="error">
                  <BaseIcon name="image" class="image--error" />
                </div>
              </el-image>
              <h3>{{ displayValue.title }}</h3>
              <span class="Jost fw-medium text-secondary">可抵用次數：<span class="text-primary">{{ `${displayValue.times}次`
              }}</span></span>
              <ul :class="$style.metadata">
                <li class="Jost">建立於 {{ displayValue.createdAt | formatDate('dateTime') }}</li>
                <li class="Jost">
                  最後編輯於
                  {{ displayValue.updatedAt | formatDate('dateTime') }}
                </li>
              </ul>
            </div>
          </div>
          <div class="Jost" :class="$style.overview">
            <div :class="[$style['overview__item'], $style.overview__detail]" @click="overviewDetailDialog">
              <div :class="$style['overview__value']"> {{ displayValue.infinity === 1 ? '無上限' :
                  displayValue.groupInventoryTotal
              }} </div>庫存
            </div>
            <div :class="$style.onlyRightBorder"></div>
            <div :class="$style['overview__item']">
              <div :class="$style['overview__value']">{{ displayValue.groupQtyTotal }}</div>已售出
            </div>
          </div>

          <el-radio-group v-model="activeTabIndex" class="is-block" :class="$style.tabs">
            <el-radio-button v-for="(tab, $index) in sideTabList" :key="tab.name" :label="$index">
              <BaseIcon :name="tab.icon" class="fs-xl align-text-top" />
              <span>{{ tab.title }}</span>
            </el-radio-button>

            <el-radio-group class="is-block" :class="$style.tabs">
              <el-radio-button :class="$style.enabled" :label="1" disabled @click.native="drawerVisible = true">
                <BaseIcon name="shopping-bag" icon-style="fal" class="fs-xl align-text-top" /> <span>代客購買方案</span>
              </el-radio-button>
              <el-radio-button v-show="false" :label="0" @click.native="drawerVisible = true"> </el-radio-button>
            </el-radio-group>
          </el-radio-group>
        </div>
        <div :class="$style.content">
          <keep-alive>
            <component :is="activeTab.component" :ref="activeTab.name" :key="activeTab.name"
              :default-value.sync="displayValue" :products="products" :category="category" :prices="prices" :tag-list="tagList"
              @renewLeftInfo="updateInfo"></component>
          </keep-alive>
        </div>
      </div>
      <el-drawer ref="drawer" class="only-close" :class="{ appStyle: appStyle }" :visible.sync="drawerVisible"
        append-to-body :size="$root.isSmallDevice ? '100%' : '90%'" destroy-on-close :modal-append-to-body="false"
        :wrapper-closable="false" custom-class="order-drawer" @closed="componentKey++">
        <PrepaidOrderForm order-type="prepaid" :prepaid-group="defaultValue" @store="handleUpdateOrders" />
      </el-drawer>
    </template>
    <!-- 庫存 -->
    <el-dialog :title="`${displayValue.title}庫存`" width="550px" :visible.sync="openOverViewDialog" top="0"
      append-to-body destroy-on-close>
      <el-table :data="gridDataComputed">
        <el-table-column property="sizeTitle" :label="`${$t('member.BODY_TO_TYPE')}`" min-width="250"></el-table-column>
        <el-table-column property="inventory" label="數量" min-width="200" align="right"></el-table-column>
      </el-table>
    </el-dialog>
  </Layout>
</template>

<style lang="scss" module>
@import '@src/design/layout_detail_sidebar.scss';

.enabled {
  :global {
    .el-radio-button__orig-radio:disabled+.el-radio-button__inner {
      color: $color-secondary;
      cursor: pointer;

      &:hover {
        color: $color-primary;
      }
    }
  }
}

.overview__detail {
  border: 1px solid $color-line-dark;
  border-radius: $border-radius;
  padding-top: 5px;
  padding-bottom: 5px;

  &:hover {
    cursor: pointer;
  }
}

.onlyRightBorder {
  border-right: 1px solid $color-line-dark;
  width: 1px;
  margin-left: 15px;
  margin-right: 15px;
  height: 20px;
}
</style>
